var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { useState, useRef, useEffect, useCallback } from 'react';
import { useUXPContext } from '@core/context/useUXPContext';
import useHasChanged from '../../../hooks/useHasChanged';
;
;
;
;
;
;
/**
 * Internal helper function to merge ExecuteConfig with dynamic parameters.
 * @internal
 */
function _mergeConfigWithParams(baseConfig, params) {
    if (!params) {
        return baseConfig;
    }
    // Create a merged config with updated parameters
    const merged = Object.assign({}, baseConfig);
    merged.parameters = Object.assign(Object.assign({}, baseConfig.parameters), params);
    return merged;
}
/**
 * Internal helper function to extract data by path string.
 * Supports dot notation and bracket notation.
 * @internal
 *
 * @example
 * ```
 * _extractByPath(data, '[0].apps')        // data[0].apps
 * _extractByPath(data, 'result.data')     // data.result.data
 * _extractByPath(data, 'items[0].name')   // data.items[0].name
 * ```
 */
function _extractByPath(data, path) {
    // Convert path to array of keys
    // '[0].apps' → [0, 'apps']
    // 'result.data' → ['result', 'data']
    // 'result[0].data' → ['result', 0, 'data']
    const keys = path
        .replace(/\[(\w+)\]/g, '.$1') // Convert [0] or [key] to .0 or .key
        .replace(/^\./, '') // Remove leading dot
        .split('.')
        .map(key => /^\d+$/.test(key) ? parseInt(key, 10) : key);
    // Traverse the path
    return keys.reduce((acc, key) => acc === null || acc === void 0 ? void 0 : acc[key], data);
}
/**
 * Internal helper function to process response data (extract and transform).
 * @internal
 */
function _processResponseData(data, extractData, transformData) {
    let processed = data;
    // Extract if needed
    if (extractData) {
        if (typeof extractData === 'function') {
            // Function extraction
            processed = extractData(data);
        }
        else if (Array.isArray(extractData)) {
            // Array chained extraction: [0, 'apps'] → data[0].apps
            processed = extractData.reduce((acc, key) => acc === null || acc === void 0 ? void 0 : acc[key], data);
        }
        else if (typeof extractData === 'string') {
            // Check if it's a path string (contains . or [)
            if (extractData.includes('.') || extractData.includes('[')) {
                processed = _extractByPath(data, extractData);
            }
            else {
                // Simple property: 'apps' → data.apps
                processed = data === null || data === void 0 ? void 0 : data[extractData];
            }
        }
    }
    // Transform if needed
    if (transformData && typeof transformData === 'function') {
        processed = transformData(processed);
    }
    return processed;
}
/**
 * Internal helper function that merges options with config and processes result data.
 * Provides DRY solution for extractData/transformData/defaultValue logic.
 * @internal
 */
function _mergeAndProcessResult(result, config, options) {
    var _a, _b, _c, _d;
    // Merge: options override config
    const mergedDefaultValue = (_b = (_a = options === null || options === void 0 ? void 0 : options.defaultValue) !== null && _a !== void 0 ? _a : config === null || config === void 0 ? void 0 : config.defaultValue) !== null && _b !== void 0 ? _b : null;
    const extractData = (_c = options === null || options === void 0 ? void 0 : options.extractData) !== null && _c !== void 0 ? _c : config === null || config === void 0 ? void 0 : config.extractData;
    const transformData = (_d = options === null || options === void 0 ? void 0 : options.transformData) !== null && _d !== void 0 ? _d : config === null || config === void 0 ? void 0 : config.transformData;
    // Process response data (extract and/or transform)
    if (result.data && (extractData || transformData)) {
        result.data = _processResponseData(result.data, extractData, transformData);
    }
    // If result has error but no data, use mergedDefaultValue
    if (result.error && !result.data) {
        result.data = mergedDefaultValue;
    }
    return result;
}
/**
 * Internal helper function that executes an ExecuteConfig and returns ExecutionResult.
 * Used by both executeConfig and useExecuteRequest hook.
 * @internal
 */
function _executeConfigInternal(context, config) {
    return __awaiter(this, void 0, void 0, function* () {
        try {
            // Switch on config type and call appropriate context method
            switch (config.type) {
                case 'action':
                    return yield context.executeActionSafe(config.model, config.action, config.parameters || {}, config.options || {});
                case 'service':
                    return yield context.executeServiceSafe(config.app, config.service, config.parameters || {}, config.options || {});
                case 'microservice':
                    return yield context.executeMicroServiceSafe(config.config, config.options || {});
                case 'api':
                    return yield context.executeAPISafe(config.config, config.options || {});
                case 'query':
                    return yield context.executeQuerySafe(config.params);
                case 'collection':
                    return yield context.executeModelCollectionServiceSafe(config.model, config.collection, config.parameters || {}, config.options || {});
                default:
                    // TypeScript exhaustiveness check
                    const _exhaustive = config;
                    throw new Error(`Unknown execution type: ${_exhaustive.type}`);
            }
        }
        catch (e) {
            // Catch any unexpected errors
            const msg = e instanceof Error ? e.message : String(e);
            return {
                data: null,
                error: e,
                errorMessage: msg
            };
        }
    });
}
/**
 * Internal helper that handles preview mode before execution.
 * Returns sample data if preview enabled, otherwise executes normally.
 * Provides DRY solution for preview logic across execution methods.
 * @internal
 */
function _executeWithPreview(context, config, preview) {
    return __awaiter(this, void 0, void 0, function* () {
        // If preview mode, return sample data immediately
        if (preview === null || preview === void 0 ? void 0 : preview.isPreview) {
            return {
                data: preview.sampleData,
                error: null,
                errorMessage: undefined
            };
        }
        // Otherwise, execute normally
        return yield _executeConfigInternal(context, config);
    });
}
/**
 * @export
 * Executes an ExecuteConfig directly using the provided context.
 * Useful for callback patterns where useExecuteRequest hook cannot be used.
 *
 * @param context - The UXP context object (from useUXPContext)
 * @param config - The execution configuration
 * @param options - Optional runtime overrides for extractData, transformData, defaultValue, and preview
 * @returns Promise resolving to the data, or throwing an error
 *
 * @example
 * ```
 * // Basic usage in a callback (e.g., ObjectSearchComponent's getAll)
 * const getAll = async (page, pageSize) => {
 *   const config = LocationServices.getAll({ page, pageSize });
 *   const data = await executeConfig(context, config);
 *   return { items: data };
 * }
 * ```
 *
 * @example
 * ```
 * // Override extraction/transformation at runtime
 * const result = await executeConfig(
 *   context,
 *   LocationServices.getAll(),
 *   { extractData: 'customPath', transformData: (data) => processData(data) }
 * );
 * ```
 *
 * @example
 * ```
 * // Preview mode with sample data
 * const result = await executeConfig(
 *   context,
 *   LocationServices.getAll(),
 *   { preview: { isPreview: true, sampleData: mockLocations } }
 * );
 * ```
 *
 * @example
 * ```
 * // Error handling
 * try {
 *   const data = await executeConfig(context, config);
 *   console.log(data);
 * } catch (error) {
 *   console.error('Failed:', error.message);
 * }
 * ```
 */
export function executeConfig(context, config, options) {
    return __awaiter(this, void 0, void 0, function* () {
        // Execute with preview support
        const result = yield _executeWithPreview(context, config, options === null || options === void 0 ? void 0 : options.preview);
        // Merge options with config and process result
        return _mergeAndProcessResult(result, config, options);
    });
}
/**
 * @export
 * Generic hook for executing safe context methods with loading state, debounce, and auto-execution.
 *
 * Returns { loading, data, error, errorMessage, execute, refresh, reset }
 * Handle success/error in your component using the returned data/error values.
 *
 * @example
 * // Auto-execute on mount - state (loading, data, error) updates automatically
 * const { loading, data, error } = useExecuteRequest({
 *   type: 'service',
 *   app: 'UXP',
 *   service: 'Portal:all'
 * });
 *
 * @example
 * // Auto-execute when dependencies change
 * const { loading, data, error } = useExecuteRequest({
 *   type: 'action',
 *   model: 'User',
 *   action: 'GetDetails',
 *   parameters: { id: userId }
 * }, { dependencies: [userId] });
 *
 * @example
 * // Use service configuration from services.ts (recommended)
 * const { loading, data, error } = useExecuteRequest(NavigationServices.getAll());
 *
 * @example
 * // Override config-level extraction/transformation
 * const { data } = useExecuteRequest(
 *   NavigationServices.getAll(),
 *   { extractData: 'customPath' }  // Overrides service-level extractData
 * );
 *
 * @example
 * // Manual execution with promise - handle result yourself (no state update)
 * const { execute } = useExecuteRequest(
 *   RouteServices.create({}),
 *   { autoExecute: false }
 * );
 * const result = await execute({ Route: '/custom', PageID: 'page-1' });
 * if (result.error) { //handle error  }
 * else {  //use result.data  }
 *
 * @example
 * // Manual execution with state update
 * const { loading, data, error, execute } = useExecuteRequest(
 *   RouteServices.update('', {}),
 *   { autoExecute: false }
 * );
 * await execute({ Key: '123', Route: '/new' }, true); // Pass true to update state
 *
 * @example
 * // UI refresh with debounce
 * const { loading, data, refresh } = useExecuteRequest(
 *   PortalServices.getAll(),
 *   { debounce: 500 }
 * );
 * <button onClick={() => refresh()}>Refresh</button> // Updates state with debounce
 * <button onClick={() => refresh(true)}>Refresh Now</button> // Updates state immediately
 *
 * @example
 * // Polling - executes immediately on mount, then every 5 seconds
 * const { loading, data, error } = useExecuteRequest(
 *   MonitoringServices.getMetrics(),
 *   { polling: 5000 }
 * );
 * // Manual refresh resets the polling timer
 * <button onClick={() => refresh()}>Refresh Now</button>
 *
 * @example
 * // Callback pattern with dynamic parameters - useful for ObjectSearchComponent
 * const { execute } = useExecuteRequest(
 *   LocationServices.getAll({ page: 1, pageSize: 50 }),
 *   { autoExecute: false }
 * );
 *
 * const getAll = async (page, pageSize, query, filters) => {
 *   const result = await execute({ page, pageSize, query, ...filters });
 *   return { items: result.data || [] };
 * }
 */
export function useExecuteRequest(config, options = {}) {
    var _a, _b, _c, _d;
    const context = useUXPContext();
    const { debounce = 0, dependencies = [], polling, defaultValue = null, preview } = options;
    const { isPreview = false, sampleData = defaultValue } = preview || {};
    const autoExecute = isPreview ? false : (options === null || options === void 0 ? void 0 : options.autoExecute) == false ? false : true;
    // Merge defaultValue, extractData and transformData: options override config
    const mergedDefaultValue = (_b = (_a = options === null || options === void 0 ? void 0 : options.defaultValue) !== null && _a !== void 0 ? _a : config === null || config === void 0 ? void 0 : config.defaultValue) !== null && _b !== void 0 ? _b : null;
    const extractData = (_c = options.extractData) !== null && _c !== void 0 ? _c : config === null || config === void 0 ? void 0 : config.extractData;
    const transformData = (_d = options.transformData) !== null && _d !== void 0 ? _d : config === null || config === void 0 ? void 0 : config.transformData;
    // State
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState(mergedDefaultValue);
    const [error, setError] = useState(null);
    const [errorMessage, setErrorMessage] = useState(undefined);
    // Refs for cleanup and race condition handling
    const debounceTimerRef = useRef(null);
    const pollingTimerRef = useRef(null);
    const requestCounterRef = useRef(0);
    const isMountedRef = useRef(true);
    const loadingRef = useRef(false);
    // Keep loadingRef in sync with loading state
    useEffect(() => {
        loadingRef.current = loading;
    }, [loading]);
    // Detect dependency changes
    const depsChanged = useHasChanged(dependencies);
    // Polling helper functions
    const clearPolling = useCallback(() => {
        if (pollingTimerRef.current) {
            clearTimeout(pollingTimerRef.current);
            pollingTimerRef.current = null;
        }
    }, []);
    // Use ref to store the latest executeInternal to avoid circular dependency
    const executeInternalRef = useRef();
    const startPolling = useCallback(() => {
        // Only start polling if autoExecute is enabled and polling interval is set
        if (!autoExecute || !polling || polling <= 0)
            return;
        clearPolling();
        pollingTimerRef.current = setTimeout(() => {
            // Prevent concurrent requests - use ref to get current loading state
            if (!loadingRef.current && executeInternalRef.current) {
                executeInternalRef.current();
            }
        }, polling);
    }, [autoExecute, polling, clearPolling]);
    // Core execution function
    const executeInternal = useCallback((...args_1) => __awaiter(this, [...args_1], void 0, function* (updateLocalState = true, params) {
        const currentRequest = ++requestCounterRef.current;
        if (updateLocalState) {
            setLoading(true);
            setError(null);
            setErrorMessage(undefined);
        }
        // Merge config with overrides if provided
        const mergedConfig = _mergeConfigWithParams(config, params);
        // Use preview-aware execution helper
        const result = yield _executeWithPreview(context, mergedConfig, isPreview ? { isPreview, sampleData } : undefined);
        // For preview mode with local state, immediately set loading to false
        if (isPreview && updateLocalState && isMountedRef.current) {
            setLoading(false);
        }
        // Use shared processing helper (merge options with config and process result)
        const processedResult = _mergeAndProcessResult(result, mergedConfig, {
            extractData,
            transformData,
            defaultValue: mergedDefaultValue
        });
        // Update result reference
        Object.assign(result, processedResult);
        // Only update state if this is still the latest request and component is mounted
        if (currentRequest === requestCounterRef.current && isMountedRef.current && updateLocalState) {
            setLoading(false);
            if (result.error) {
                setError(result.error);
                setErrorMessage(result.errorMessage);
                setData(null);
            }
            else {
                setData(result.data);
                setError(null);
                setErrorMessage(undefined);
            }
        }
        // Start polling after execution completes (if enabled)
        if (currentRequest === requestCounterRef.current && isMountedRef.current) {
            startPolling();
        }
        return result;
    }), [config, context, startPolling, mergedDefaultValue, isPreview, sampleData, extractData, transformData]);
    // Keep executeInternalRef in sync with executeInternal
    useEffect(() => {
        executeInternalRef.current = executeInternal;
    }, [executeInternal]);
    // Execute with optional debounce
    const executeWithDebounce = useCallback(() => {
        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
        }
        if (debounce > 0) {
            debounceTimerRef.current = setTimeout(() => {
                executeInternal();
            }, debounce);
        }
        else {
            executeInternal();
        }
    }, [debounce, executeInternal]);
    // public function to refresh local states
    const refresh = useCallback((executeImmediately = false) => {
        clearPolling(); // Reset polling timer on manual refresh
        if (executeImmediately)
            executeInternal();
        else
            executeWithDebounce();
    }, [executeWithDebounce, executeInternal, clearPolling]);
    // Public execute function (returns promise)
    const execute = useCallback((params, updateLocalState = false) => {
        clearPolling(); // Reset polling timer on manual execution
        return executeInternal(updateLocalState, params);
    }, [executeInternal, clearPolling]);
    // Reset function
    const reset = useCallback(() => {
        setLoading(false);
        setData(null);
        setError(null);
        setErrorMessage(undefined);
        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
        }
        clearPolling();
    }, [clearPolling]);
    // Auto-execute on mount or dependency changes
    useEffect(() => {
        if (!autoExecute)
            return;
        // Execute on initial mount or when dependencies actually change
        if (depsChanged) {
            executeWithDebounce();
        }
    }, [autoExecute, depsChanged, executeWithDebounce]);
    // Cleanup on unmount
    useEffect(() => {
        return () => {
            isMountedRef.current = false;
            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current);
            }
            clearPolling();
        };
    }, [clearPolling]);
    return {
        loading,
        data,
        error,
        errorMessage,
        execute,
        refresh,
        reset
    };
}
/**
 * @export
 * Lightweight callback wrapper around `useExecuteRequest`.
 *
 * Returns a stable async function that executes the provided config
 * **without triggering loading/data/error state updates**.
 *
 * Useful for:
 * - Passing execution callbacks to components (e.g. search, autocomplete, tables)
 * - Dynamic parameter execution
 * - One-off requests where local state management is not needed
 *
 * Internally:
 * - Forces `autoExecute = false`
 * - Disables debounce and polling
 * - Delegates execution to `useExecuteRequest`
 *
 * @example
 * // Callback-style execution (recommended for ObjectSearchComponent)
 * const getLocations = useExecuteRequestCallback(
 *   LocationServices.getAll({ page: 1, pageSize: 50 })
 * );
 *
 * const result = await getLocations({ query: 'Colombo' });
 * return result.data || [];
 *
 * @returns A function that executes the request and returns an ExecutionResult
 */
export function useExecuteRequestCallback(config, options) {
    const { execute } = useExecuteRequest(config, Object.assign(Object.assign({}, options || {}), { autoExecute: false }));
    return (params) => execute(params, false);
}
